import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Divider,
  CircularProgress,
  Chip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useDevAuth } from '../utils/useDevAuth';
import { useAxiosClient } from '../utils/useAxiosClient';
import BusinessIcon from '@mui/icons-material/Business';
import PeopleIcon from '@mui/icons-material/People';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ChatIcon from '@mui/icons-material/Chat';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';

/**
 * Admin Dashboard - Master page for all admin functions
 * 
 * Navigation to:
 * - Organizations (existing functionality)
 * - Users (placeholder)
 * - Dashboard Usage/Logs (placeholder)
 * - Chatbot Usage/Logs (placeholder)
 * - Social Media Summaries (placeholder)
 */

interface NavCard {
  title: string;
  description: string;
  icon: React.ReactNode;
  path: string;
  color: string;
}

const navCards: NavCard[] = [
  {
    title: 'Organizations',
    description: 'Manage customer organizations, settings, and access',
    icon: <BusinessIcon sx={{ fontSize: 40 }} />,
    path: '/admin/organizations',
    color: '#1976d2',
  },
  {
    title: 'Users',
    description: 'View and manage user accounts and permissions',
    icon: <PeopleIcon sx={{ fontSize: 40 }} />,
    path: '/admin/users',
    color: '#388e3c',
  },
  {
    title: 'Dashboard Logs',
    description: 'Track dashboard usage, page views, and user activity',
    icon: <DashboardIcon sx={{ fontSize: 40 }} />,
    path: '/admin/dashboard-logs',
    color: '#f57c00',
  },
  {
    title: 'Chatbot Logs',
    description: 'Monitor IL-2 chatbot conversations and performance',
    icon: <ChatIcon sx={{ fontSize: 40 }} />,
    path: '/admin/chatbot-logs',
    color: '#7b1fa2',
  },
  {
    title: 'Social Summaries',
    description: 'View aggregated social media performance summaries',
    icon: <TrendingUpIcon sx={{ fontSize: 40 }} />,
    path: '/admin/social-summaries',
    color: '#c62828',
  },
  {
    title: 'API Status',
    description: 'Collector runs, one-off collections, failing accounts and YouTube quota',
    icon: <MonitorHeartIcon sx={{ fontSize: 40 }} />,
    path: '/admin/api-status',
    color: '#00838f',
  },
];

interface ChatStats {
  total_conversations: number;
  total_messages: number;
  conversations_last_7_days: number;
  conversations_by_org: { org_name: string; count: number }[];
  most_active_users: { email: string; count: number }[];
}

interface RecentConversation {
  id: string;
  title: string | null;
  organization_name: string;
  user_email: string;
  message_count: number;
  updated_at: string;
}

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useDevAuth();
  const axiosClient = useAxiosClient();

  // State for chat stats
  const [chatStats, setChatStats] = useState<ChatStats | null>(null);
  const [recentConversations, setRecentConversations] = useState<RecentConversation[]>([]);
  const [loading, setLoading] = useState(true);

  // Check if user is admin
  const provider = user?.sub?.split('|')[0];
  const emailLower = user?.email?.toLowerCase() || '';
  const isAdmin = (provider === 'google-oauth2' || provider === 'clerk') && (emailLower === 'weezilla@gmail.com' || emailLower.endsWith('@crant.ai'));

  // Fetch chat data
  const fetchChatData = useCallback(async () => {
    if (!isAdmin) return;
    
    setLoading(true);
    try {
      const [statsRes, convosRes] = await Promise.all([
        axiosClient.get('/api/admin/conversations/stats/summary'),
        axiosClient.get('/api/admin/conversations?limit=5'),
      ]);
      setChatStats(statsRes.data);
      setRecentConversations(convosRes.data.conversations || []);
    } catch (error) {
      console.error('Failed to fetch chat data:', error);
    } finally {
      setLoading(false);
    }
  }, [axiosClient, isAdmin]);

  useEffect(() => {
    fetchChatData();
  }, [fetchChatData]);

  // Format relative time
  const formatRelativeTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4">Unauthorized</Typography>
        <Typography>You do not have access to this page.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1400, margin: '0 auto' }}>
      <Typography variant="h4" sx={{ mb: 1, fontWeight: 600 }}>
        Admin Dashboard
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Manage organizations, users, and monitor platform activity
      </Typography>

      {/* Navigation Cards */}
      <Grid container spacing={3} sx={{ mb: 5 }}>
        {navCards.map((card) => (
          <Grid item xs={12} sm={6} md={4} lg={2.4} key={card.title}>
            <Card 
              sx={{ 
                height: '100%',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4,
                },
              }}
            >
              <CardActionArea 
                onClick={() => navigate(card.path)}
                sx={{ height: '100%', p: 2 }}
              >
                <CardContent sx={{ textAlign: 'center' }}>
                  <Box sx={{ color: card.color, mb: 1 }}>
                    {card.icon}
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    {card.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {card.description}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Divider sx={{ mb: 4 }} />

      {/* Summary Section */}
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
        Quick Stats
      </Typography>

      <Grid container spacing={3}>
        {/* Active Customers */}
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 3, textAlign: 'center', bgcolor: '#e3f2fd' }}>
            <Typography variant="h3" sx={{ fontWeight: 700, color: '#1976d2' }}>
              --
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Active Customers
            </Typography>
            <Typography variant="caption" color="text.secondary">
              (Coming soon)
            </Typography>
          </Paper>
        </Grid>

        {/* Total Users */}
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 3, textAlign: 'center', bgcolor: '#e8f5e9' }}>
            <Typography variant="h3" sx={{ fontWeight: 700, color: '#388e3c' }}>
              --
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Total Users
            </Typography>
            <Typography variant="caption" color="text.secondary">
              (Coming soon)
            </Typography>
          </Paper>
        </Grid>

        {/* Dashboard Sessions Today */}
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 3, textAlign: 'center', bgcolor: '#fff3e0' }}>
            <Typography variant="h3" sx={{ fontWeight: 700, color: '#f57c00' }}>
              --
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Sessions Today
            </Typography>
            <Typography variant="caption" color="text.secondary">
              (Coming soon)
            </Typography>
          </Paper>
        </Grid>

        {/* Chatbot Conversations */}
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 3, textAlign: 'center', bgcolor: '#f3e5f5' }}>
            <Typography variant="h3" sx={{ fontWeight: 700, color: '#7b1fa2' }}>
              {loading ? <CircularProgress size={32} /> : chatStats?.total_conversations || 0}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Chat Conversations
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {chatStats?.conversations_last_7_days || 0} in last 7 days
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Recent Activity Placeholder */}
      <Box sx={{ mt: 5 }}>
        <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
          Recent Activity
        </Typography>

        <Grid container spacing={3}>
          {/* Recent Dashboard Access */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, minHeight: 250 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Recent Dashboard Access
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                height: 150,
                color: 'text.secondary',
                bgcolor: '#f5f5f5',
                borderRadius: 1,
              }}>
                <Typography variant="body2">
                  Activity log coming soon...
                </Typography>
              </Box>
            </Paper>
          </Grid>

          {/* Recent Chatbot Conversations */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, minHeight: 250 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Recent Chatbot Conversations
                </Typography>
                <Typography 
                  variant="caption" 
                  sx={{ color: '#7b1fa2', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                  onClick={() => navigate('/admin/chatbot-logs')}
                >
                  View all →
                </Typography>
              </Box>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : recentConversations.length === 0 ? (
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  height: 150,
                  color: 'text.secondary',
                  bgcolor: '#f5f5f5',
                  borderRadius: 1,
                }}>
                  <Typography variant="body2">
                    No conversations yet
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {recentConversations.map((conv) => (
                    <Box 
                      key={conv.id}
                      sx={{ 
                        p: 1.5, 
                        borderRadius: 1, 
                        bgcolor: '#fafafa',
                        cursor: 'pointer',
                        '&:hover': { bgcolor: '#f0f0f0' },
                      }}
                      onClick={() => navigate('/admin/chatbot-logs')}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: 500,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: '60%',
                          }}
                        >
                          {conv.title || 'Untitled conversation'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatRelativeTime(conv.updated_at)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                        <Chip label={conv.organization_name} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />
                        <Chip label={`${conv.message_count} msgs`} size="small" sx={{ fontSize: '0.65rem', height: 20 }} />
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default AdminDashboard;

